import { deepClone } from "@repo/lib/deep";
import type {
  AgendaItem,
  AgendaItemMotion,
  AgendaModule,
  Meeting,
  MinutesOrdering,
  TeamMember,
} from "@repo/types";

export interface getMeetingMinutesDataParams {
  meeting?: Meeting;
  agendaItemMap?: Record<string, AgendaItem>;
  submitter?: TeamMember;
  order?: MinutesOrdering;
}

export type MeetingMinutesData = (
  | AgendaModule
  | AgendaItem
  | AgendaItemMotion
  | TeamMember
)[];

export const getMeetingMinutesData = ({
  meeting,
  agendaItemMap = {},
  submitter,
  order = "timestamps",
}: getMeetingMinutesDataParams): MeetingMinutesData => {
  if (!meeting) return [];

  // clone all to avoid modifying original objects
  const meetingClone = deepClone(meeting);
  const agendaItemMapClone = deepClone(agendaItemMap);

  // coalesce meeting motions
  meetingClone.review ??= {};
  const meetingMotions = (meetingClone.review.motions ??= []).slice();

  // prepare to find all unhandled agendaItems
  const unhandledItems: AgendaItem[] = [];

  const unseenSections = new Set<AgendaModule>();
  const itemSections = new Map<string, AgendaModule>();

  // merge all agendaItems from agendaModule.agendaItems arrays
  let prevItem: AgendaItem | null = null;
  const missingTimes: AgendaItem[] = [];

  let agendaItems =
    meetingClone?.agenda?.agendaModules?.reduce((allItems, agendaModule) => {
      const agendaItems = (agendaModule.agendaItems ??= []);

      unseenSections.add(agendaModule);

      for (let i = 0; i < agendaItems.length; i++) {
        let agendaItem = agendaItems[i];
        itemSections.set(agendaItem.id, agendaModule);

        if (agendaItem.id in agendaItemMapClone) {
          agendaItems[i] = agendaItem = agendaItemMapClone[agendaItem.id];
        }
        // coalesce review and review.motions for all agendaItems
        agendaItem.review ??= { meetingId: meeting.id };
        agendaItem.review.motions ??= [];

        if (!agendaItem.review.timestamp || !agendaItem.review.timestampEnd) {
          // if one timestamp is set, copy it to the other
          if (agendaItem.review.timestamp) {
            agendaItem.review.timestampEnd = agendaItem.review.timestamp;
          } else if (agendaItem.review.timestampEnd) {
            agendaItem.review.timestamp = agendaItem.review.timestampEnd;
          } else if (
            (agendaItem.review.status &&
              agendaItem.review.status !== "pending") ||
            agendaItem.review.moved
          ) {
            if (prevItem) {
              agendaItem.review.timestamp = agendaItem.review.timestampEnd =
                prevItem.review!.timestampEnd! + 1;
            } else {
              missingTimes.push(agendaItem);
            }
          } else {
            // agendaItems was not handled
            unhandledItems.push(agendaItem);
          }
        }

        if (agendaItem.review.timestamp) {
          // if some resolved items at the start had no recorded timestamps...
          if (missingTimes.length) {
            // ...then provide timestamps from this item by inserting them before,
            // in order of their occurrence
            let i = missingTimes.length;
            for (const item of missingTimes) {
              item.review!.timestamp = item.review!.timestampEnd =
                agendaItem.review.timestamp - i;
              i--;
            }
            missingTimes.length = 0;
          }
          prevItem = agendaItem;
        }
        allItems.push(agendaItem);
      }

      return allItems;
    }, [] as AgendaItem[]) ?? [];

  // if no timestamps are available, then
  // place each item 1 minute apart, starting at the meeting start
  if (missingTimes.length) {
    let time = meetingClone.startTime.getTime();
    for (const item of missingTimes) {
      item.review!.timestamp = item.review!.timestampEnd = time;
      time += 1000 * 60;
    }
  }

  agendaItems = agendaItems.filter(
    (agendaItem) =>
      agendaItem.review!.timestamp && agendaItem.review!.timestampEnd
  );

  // sort agendaItems by timestamp
  let timeSortedItems: AgendaItem[] = [];
  if (order === "timestamps") {
    agendaItems.sort((a, b) => a.review!.timestamp! - b.review!.timestamp!);
    timeSortedItems = agendaItems.slice();
  } else {
    timeSortedItems = agendaItems
      .slice()
      .sort((a, b) => a.review!.timestamp! - b.review!.timestamp!);
  }

  // find the item that chronologically follows the given item
  const nextItemInTime = (item?: AgendaItem | null): AgendaItem | undefined => {
    if (!item) return timeSortedItems[0];
    const index = timeSortedItems.indexOf(item);
    return timeSortedItems[index + 1];
  };

  // sort meeting motions by timestamp
  meetingMotions.sort((a, b) => a.timestamp - b.timestamp);

  // interleave meeting motions with agendaItem motions

  // insert meeting motions with a timestamp between 'from' and 'to' at position 'index' in 'array'
  // returns the number of motions inserted
  const insertMeetingMotions = (
    from: number,
    to: number,
    arrayFrom: AgendaItemMotion[],
    arrayInto: unknown[],
    index: number
  ): number => {
    let count = 0;

    for (let i = 0; i < arrayFrom.length; i++) {
      const motion = arrayFrom[i];
      if (motion.timestamp >= from && motion.timestamp < to) {
        count++;
        arrayInto.splice(index++, 0, motion);
        arrayFrom.splice(i--, 1);
      } else if (count) {
        break;
      }
    }
    return count;
  };

  // recursively iterate nested motions and insert any relevant meeting motions
  const iterateNestedMotions = (
    motions: AgendaItemMotion[],
    meetingMotions: AgendaItemMotion[],
    from: number,
    to: number
  ) => {
    for (let i = 0; i <= motions.length; i++) {
      // iterate nested motions of motions[i - 1], if any
      if (i > 0) {
        const motion = motions[i - 1];
        iterateNestedMotions(
          (motion.nesting ??= []),
          meetingMotions,
          motion.timestamp,
          motion.timestampEnd ?? motions[i]?.timestamp ?? to
        );
      }

      // insert relevant meeting motions between motions[i - 1] and motions[i]
      i += insertMeetingMotions(
        i > 0 ? motions[i - 1].timestamp : from,
        i < motions.length ? motions[i].timestamp : to,
        meetingMotions,
        motions,
        i
      );
    }
  };

  const agendaMotions: MeetingMinutesData = [];

  // collect meeting motions into items they occurred during
  const outerMeetingMotions: AgendaItemMotion[] = []; // a list of meeting motions that didn't occur during an AgendaItem
  const meetingMotionsByItem = new Map<AgendaItem, AgendaItemMotion[]>(); // maps AgendaItems to arrays of meeting motions occurring during them

  for (const motion of meetingMotions) {
    const containing = agendaItems.filter(
      (agendaItem) =>
        agendaItem.review!.timestamp! <= motion.timestamp &&
        agendaItem.review!.timestampEnd! > motion.timestamp
    );
    if (!containing.length) {
      outerMeetingMotions.push(motion);
      continue;
    }

    const item = containing.reduce(
      (earliestEnding, agendaItem) =>
        !earliestEnding ||
        agendaItem.review!.timestampEnd! < earliestEnding.review!.timestampEnd!
          ? agendaItem
          : earliestEnding,
      null as null | AgendaItem
    )!;
    const motionArray = meetingMotionsByItem.get(item) ?? [];
    motionArray.push(motion);
    meetingMotionsByItem.set(item, motionArray);
  }

  // iterate agendaItems and insert meeting motions where relevant, between items or between item motions
  prevItem = null;

  const processItem = (agendaItem: AgendaItem) => {
    agendaMotions.push(agendaItem);

    const nextTimeItem = nextItemInTime(prevItem);

    // insert meeting motions occurring between the times of the previous item and the
    // chronologically next item
    if (nextTimeItem) {
      insertMeetingMotions(
        prevItem?.review!.timestamp ?? 0,
        nextTimeItem.review!.timestamp!,
        outerMeetingMotions,
        agendaMotions,
        agendaMotions.length
      );
    }

    // insert meeting motions between item motions of agendaItems[i], if any
    const motions = agendaItem.review!.motions!;
    const meetingMotions = meetingMotionsByItem.get(agendaItem);
    if (meetingMotions) {
      iterateNestedMotions(
        motions,
        meetingMotions,
        agendaItem.review!.timestamp!,
        agendaItem.review!.timestampEnd!
      );
    }

    prevItem = agendaItem;
  };

  if (order === "agenda") {
    for (const agendaModule of meetingClone?.agenda?.agendaModules ?? []) {
      // insert agendaModule section headers
      agendaMotions.push(agendaModule);

      for (const agendaItem of agendaModule.agendaItems ?? []) {
        if (agendaItem.review?.timestamp) processItem(agendaItem);
      }
    }
  } else if (order === "timestamps") {
    for (const agendaItem of agendaItems) {
      // insert agendaModule section headers
      const agendaModule = itemSections.get(agendaItem.id);
      if (agendaModule && unseenSections.has(agendaModule)) {
        agendaMotions.push(agendaModule);
        unseenSections.delete(agendaModule);
      }

      processItem(agendaItem);
    }
  } else {
    throw new Error(`Invalid value for minutes order: "${order}"`);
  }

  // insert meeting motions occurring after all agendaItems at end
  agendaMotions.push(...outerMeetingMotions);

  if (submitter) agendaMotions.push(submitter);

  return agendaMotions;
};
